import classNames from "classnames";
import React, { ReactElement, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Column,
  ColumnInstance,
  Filters,
  Hooks,
  PluginHook,
  Row,
  SortingRule,
  useAsyncDebounce,
  useGlobalFilter,
  UseGlobalFiltersColumnOptions,
  UseGlobalFiltersOptions,
  usePagination,
  UsePaginationOptions,
  useRowSelect,
  useSortBy,
  UseSortByColumnOptions,
  UseSortByOptions,
  useTable,
  UseTableOptions,
} from "react-table";
import { IconButton } from "../button";
import { Checkbox } from "../checkbox/checkbox";
import { IconDownload, IconUpload } from "../icons";
import { ColorOption } from "../types";
import { CSS_CLASSES } from "./table-css-classes";
import { PaginationOptions, TablePagination } from "./table-pagination";

/**
 * Table supports all color options except black and white.
 */
export type TableColorOption = Exclude<ColorOption, "white" | "black">;

/**
 * Allowed variants for Tag.
 */

export type TableVariants = "light" | "heavy";

export interface DataRowProps {
  rowFooter?: boolean;
  highlighted?: TableColorOption;
}

export type TableRowData = { [key in string]: any } & DataRowProps;

export type TableColumn<D extends TableRowData> = Column<D> &
  UseSortByColumnOptions<D> &
  UseGlobalFiltersColumnOptions<D>;

export type StateChangeArg<D extends TableRowData> = {
  pageIndex: number;
  pageSize: number;
  sortBy: SortingRule<D>[];
  filters: Filters<D>;
  globalFilter: any;
};

export interface TableProps<D extends TableRowData>
  extends Pick<UseTableOptions<D>, "data" | "columns">,
    Partial<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> {
  /**
   * This prop controls the stylistic variant of the table.
   * Each variant has a different UX intent.
   * @default light
   */
  variant: TableVariants;

  /**
   * Each row with truthy `rowFooter` property will have additional content rendered by `rowFooterRenderer` component.
   * This component accept all row properties as a props
   */
  rowFooterRenderer?: (row: Row<D>) => JSX.Element;

  /**
   * Controls the background color of odd table body rows
   * all the possible values defined in Blueprint design system
   *
   */
  striped?: TableColorOption;

  /**
   * Controls the background color of the hovered rows
   * all the possible values defined in Blueprint design system
   *
   */
  hover?: TableColorOption;

  /**
   * This prop controls if sorting shall be handled inside table.
   * See [useSortBy](https://github.com/tannerlinsley/react-table/blob/master/docs/api/useSortBy.md) api for reference
   *
   * @default true
   *
   */
  sortable?: boolean;

  /**
   * This prop controls if global filter shall be handled inside table.
   * See [useGlobalFilter](https://github.com/tannerlinsley/react-table/blob/master/docs/api/useGlobalFilter.md) api for reference
   *
   */
  globalFilterable?: boolean;
  /**
   * This prop controls if row selections shall be handled inside table.
   * See [useRowSelect](https://github.com/tannerlinsley/react-table/blob/master/docs/api/useRowSelect.md) api for reference
   *
   */
  selectable?: boolean;

  /**
   * Handler is executed whenever array with selected rows has changed
   */
  onRowsSelect?: (selectedRows: Row<D>[]) => void;

  /**
   * This prop controls if pagination shall be handled inside table.
   * See [usePagination](https://github.com/tannerlinsley/react-table/blob/master/docs/api/usePagination.md) api for reference
   *
   * @default true
   *
   */
  paginate?: boolean;

  /**
   * All options passed here will be spread onto Pagination component
   *
   */
  paginationOptions?: PaginationOptions;

  /**
   * Required if table shall handle pagination and `tableOptions.manualPagination` is set to `true`
   *
   */
  totalItems?: number;

  /**
   * This prop controls if the table rendered on mobile devices uses rows (default) or columns for the layout.
   *
   * @default false
   *
   */
  useColumnLayoutMobile?: boolean;

  /**
   * Use this this prop to handle server-side sorting, pagination and etc.. All options passed here will be spread as 'useTable' options
   * See [react-table](https://github.com/tannerlinsley/react-table/blob/master/docs/api/README.md) api for reference
   *
   */
  tableOptions?: Omit<UseTableOptions<D>, "columns" | "data"> &
    UseSortByOptions<D> &
    UsePaginationOptions<D> &
    UseGlobalFiltersOptions<D>;

  onStateChange?: (state: StateChangeArg<D>) => void;

  controlledState?: Partial<StateChangeArg<D>>;

  /**
   * Controls the additional classNames passed to the component styling
   * This cnm be a string containing multiple classes space separated
   */
  className?: string;
}

/**
 * A `Table` displays rows of data
 */
export const Table = <D extends TableRowData>({
  data,
  columns,
  variant = "light",
  rowFooterRenderer,
  sortable = true,
  paginate = true,
  globalFilterable,
  selectable,
  hover,
  striped,
  totalItems: manualTotalItems,
  useColumnLayoutMobile = false,
  paginationOptions = {},
  tableOptions,
  onStateChange = () => {},
  onRowsSelect = () => {},
  controlledState,
  className,
  ...restProps
}: TableProps<D>): ReactElement => {
  const hooks = useMemo<PluginHook<D>[]>(
    () => [
      ...(globalFilterable ? [useGlobalFilter] : []),
      ...(sortable ? [useSortBy] : []),
      ...(paginate ? [usePagination] : []),
      ...(selectable
        ? [
            useRowSelect,
            (hooks: Hooks<D>) => {
              hooks.visibleColumns.push((columns) => [
                {
                  id: "selection",
                  Header: ({ getToggleAllRowsSelectedProps }) => <Checkbox {...getToggleAllRowsSelectedProps()} />,
                  Cell: ({ row }) => <Checkbox {...row.getToggleRowSelectedProps()} />,
                } as ColumnInstance<D>,
                ...columns,
              ]);
            },
          ]
        : []),
    ],
    [sortable, paginate, globalFilterable, selectable]
  );

  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    visibleColumns,
    gotoPage,
    setPageSize,
    page,
    selectedFlatRows,
    state: { pageIndex, pageSize, sortBy, filters, globalFilter },
  } = useTable<D>(
    {
      data,
      columns,
      defaultColumn: {
        sortDescFirst: true,
      },
      useControlledState: (state) => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        return React.useMemo(() => ({ ...state, ...controlledState }), [state, controlledState]);
      },
      ...tableOptions,
    },
    ...hooks
  );

  const totalItems = useMemo(() => {
    return manualTotalItems || rows.length;
  }, [rows.length, manualTotalItems]);

  const tableContainerClasses = useMemo(
    () =>
      classNames(CSS_CLASSES.TABLE_CONTAINER, className, {
        [CSS_CLASSES.TABLE_HEAVY_CONTAINER]: variant === "heavy",
      }),
    [className, variant]
  );
  const tableClasses = useMemo(
    () =>
      classNames(
        CSS_CLASSES.TABLE_ROOT,
        ...(variant === "light" ? [CSS_CLASSES.TABLE_LIGHT_ROOT] : [CSS_CLASSES.TABLE_HEAVY_ROOT])
      ),
    [variant]
  );

  const tableMobileCardClasses = useMemo(() => classNames(CSS_CLASSES.TABLE_MOBILE_CARD), []);

  const getTableMobileCardRowHighlightedClassName = useCallback(
    (highlighted: TableColorOption) =>
      classNames({
        [CSS_CLASSES.TABLE_MOBILE_CARD_PRIMARY_HIGHLIGHTED]: highlighted === "primary",
        [CSS_CLASSES.TABLE_MOBILE_CARD_SECONDARY_HIGHLIGHTED]: highlighted === "secondary",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ACCENT_HIGHLIGHTED]: highlighted === "accent",
        [CSS_CLASSES.TABLE_MOBILE_CARD_GRAY_HIGHLIGHTED]: highlighted === "gray",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ERROR_HIGHLIGHTED]: highlighted === "error",
        [CSS_CLASSES.TABLE_MOBILE_CARD_WARNING_HIGHLIGHTED]: highlighted === "warning",
        [CSS_CLASSES.TABLE_MOBILE_CARD_SUCCESS_HIGHLIGHTED]: highlighted === "success",
      }),
    []
  );

  const tableMobileCardOddClasses = useMemo(
    () =>
      classNames({
        [CSS_CLASSES.TABLE_MOBILE_CARD_PRIMARY_ODD]: striped === "primary",
        [CSS_CLASSES.TABLE_MOBILE_CARD_SECONDARY_ODD]: striped === "secondary",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ACCENT_ODD]: striped === "accent",
        [CSS_CLASSES.TABLE_MOBILE_CARD_GRAY_ODD]: striped === "gray",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ERROR_ODD]: striped === "error",
        [CSS_CLASSES.TABLE_MOBILE_CARD_WARNING_ODD]: striped === "warning",
        [CSS_CLASSES.TABLE_MOBILE_CARD_SUCCESS_ODD]: striped === "success",
      }),
    [striped]
  );

  const tableMobileCardStripedRowClasses = useMemo(
    () =>
      classNames({
        [CSS_CLASSES.TABLE_MOBILE_CARD_ROW_PRIMARY]: striped === "primary",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ROW_SECONDARY]: striped === "secondary",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ROW_ACCENT]: striped === "accent",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ROW_GRAY]: striped === "gray",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ROW_ERROR]: striped === "error",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ROW_WARNING]: striped === "warning",
        [CSS_CLASSES.TABLE_MOBILE_CARD_ROW_SUCCESS]: striped === "success",
      }),
    [striped]
  );

  const tableMobileCardRowClasses = useMemo(() => classNames(CSS_CLASSES.TABLE_MOBILE_CARD_ROW), []);

  const tableMobileCardRowKeyClasses = useMemo(() => classNames(CSS_CLASSES.TABLE_MOBILE_CARD_ROW_KEY), []);

  const tableMobileCardRowValueClasses = useMemo(() => classNames(CSS_CLASSES.TABLE_MOBILE_CARD_ROW_VALUE), []);

  const tableHeadClasses = useMemo(
    () =>
      classNames(
        CSS_CLASSES.TABLE_HEAD,
        ...(variant === "light"
          ? [CSS_CLASSES.TABLE_LIGHT_HEAD]
          : variant === "heavy"
          ? [CSS_CLASSES.TABLE_HEAVY_HEAD]
          : [])
      ),
    [variant]
  );

  const tableBodyClasses = useMemo(
    () =>
      classNames(
        CSS_CLASSES.TABLE_BODY,
        ...(variant === "light"
          ? [CSS_CLASSES.TABLE_LIGHT_BODY]
          : variant === "heavy"
          ? [CSS_CLASSES.TABLE_HEAVY_BODY]
          : [])
      ),
    [variant]
  );

  const tableBodyRowClasses = useMemo(
    () =>
      classNames(
        CSS_CLASSES.TABLE_BODY_ROW,
        ...(variant === "light"
          ? [CSS_CLASSES.TABLE_LIGHT_BODY_ROW]
          : variant === "heavy"
          ? [CSS_CLASSES.TABLE_HEAVY_BODY_ROW]
          : []),
        {
          [CSS_CLASSES.TABLE_BODY_ROW_PRIMARY_HOVER]: hover === "primary",
          [CSS_CLASSES.TABLE_BODY_ROW_SECONDARY_HOVER]: hover === "secondary",
          [CSS_CLASSES.TABLE_BODY_ROW_ACCENT_HOVER]: hover === "accent",
          [CSS_CLASSES.TABLE_BODY_ROW_GRAY_HOVER]: hover === "gray",
          [CSS_CLASSES.TABLE_BODY_ROW_ERROR_HOVER]: hover === "error",
          [CSS_CLASSES.TABLE_BODY_ROW_WARNING_HOVER]: hover === "warning",
          [CSS_CLASSES.TABLE_BODY_ROW_SUCCESS_HOVER]: hover === "success",
        }
      ),
    [variant, hover]
  );
  const tableBodyRowOddClasses = useMemo(
    () =>
      classNames({
        [CSS_CLASSES.TABLE_BODY_ROW_PRIMARY_ODD]: striped === "primary",
        [CSS_CLASSES.TABLE_BODY_ROW_SECONDARY_ODD]: striped === "secondary",
        [CSS_CLASSES.TABLE_BODY_ROW_ACCENT_ODD]: striped === "accent",
        [CSS_CLASSES.TABLE_BODY_ROW_GRAY_ODD]: striped === "gray",
        [CSS_CLASSES.TABLE_BODY_ROW_ERROR_ODD]: striped === "error",
        [CSS_CLASSES.TABLE_BODY_ROW_WARNING_ODD]: striped === "warning",
        [CSS_CLASSES.TABLE_BODY_ROW_SUCCESS_ODD]: striped === "success",
      }),
    [striped]
  );
  const getTableBodyRowHighlightedClassName = useCallback(
    (highlighted: TableColorOption) =>
      classNames({
        [CSS_CLASSES.TABLE_BODY_ROW_PRIMARY_HIGHLIGHTED]: highlighted === "primary",
        [CSS_CLASSES.TABLE_BODY_ROW_SECONDARY_HIGHLIGHTED]: highlighted === "secondary",
        [CSS_CLASSES.TABLE_BODY_ROW_ACCENT_HIGHLIGHTED]: highlighted === "accent",
        [CSS_CLASSES.TABLE_BODY_ROW_GRAY_HIGHLIGHTED]: highlighted === "gray",
        [CSS_CLASSES.TABLE_BODY_ROW_ERROR_HIGHLIGHTED]: highlighted === "error",
        [CSS_CLASSES.TABLE_BODY_ROW_WARNING_HIGHLIGHTED]: highlighted === "warning",
        [CSS_CLASSES.TABLE_BODY_ROW_SUCCESS_HIGHLIGHTED]: highlighted === "success",
      }),
    []
  );

  const tableHeadRowClasses = useMemo(
    () =>
      classNames(
        CSS_CLASSES.TABLE_HEAD_ROW,
        ...(variant === "light"
          ? [CSS_CLASSES.TABLE_LIGHT_HEAD_ROW]
          : variant === "heavy"
          ? [CSS_CLASSES.TABLE_HEAVY_HEAD_ROW]
          : [])
      ),
    [variant]
  );

  const tableBodyCellClasses = useMemo(
    () =>
      classNames(
        CSS_CLASSES.TABLE_BODY_CELL,
        ...(variant === "light"
          ? [CSS_CLASSES.TABLE_LIGHT_BODY_CELL]
          : variant === "heavy"
          ? [CSS_CLASSES.TABLE_HEAVY_BODY_CELL]
          : [])
      ),
    [variant]
  );

  const tableHeadCellClasses = useMemo(
    () =>
      classNames(
        CSS_CLASSES.TABLE_HEAD_CELL,
        ...(variant === "light"
          ? [CSS_CLASSES.TABLE_LIGHT_HEAD_CELL]
          : variant === "heavy"
          ? [CSS_CLASSES.TABLE_HEAVY_HEAD_CELL]
          : [])
      ),
    [variant]
  );

  const isInitialMount = useRef(true);

  // Debounce our onStateChange call for 100ms
  const onStateChangeDebounced = useAsyncDebounce(onStateChange, 100);

  // When the these table states changes, trigger onStateChange
  useEffect(() => {
    if (!isInitialMount.current) {
      // Every change will call our debounced function
      onStateChangeDebounced({ pageIndex, pageSize, sortBy, filters, globalFilter });
      // Only the last call after the 100ms debounce is over will be fired
    }
  }, [onStateChangeDebounced, pageIndex, pageSize, sortBy, filters, globalFilter]);

  useEffect(() => {
    if (!isInitialMount.current) {
      onRowsSelect && onRowsSelect(selectedFlatRows);
    }
  }, [selectedFlatRows, onRowsSelect]);

  useEffect(() => {
    isInitialMount.current = false;
  }, []);

  return (
    <div className={tableContainerClasses} {...restProps}>
      {useColumnLayoutMobile ? (
        <table {...getTableProps()} className={`${tableClasses} ${CSS_CLASSES.TABLE_MOBILE_COL_ROOT}`}>
          <thead className={tableHeadClasses}>
            {headerGroups.map((headerGroup) => {
              return (
                <tr {...headerGroup.getHeaderGroupProps()} className={tableHeadRowClasses}>
                  {headerGroup.headers.map((column) => {
                    if (column.hideOnMobile) {
                      return null;
                    }
                    return (
                      <th {...column.getHeaderProps()} className={tableHeadCellClasses}>
                        {sortable && column.canSort ? (
                          <div className={CSS_CLASSES.TABLE_HEAD_CELL_SORTABLE}>
                            {column.render("Header")}
                            <IconButton
                              {...column.getSortByToggleProps()}
                              variant="ghost"
                              size="small"
                              icon={
                                column.isSorted ? (
                                  column.isSortedDesc ? (
                                    <IconDownload />
                                  ) : (
                                    <IconUpload />
                                  )
                                ) : (
                                  <IconDownload color="gray" />
                                )
                              }
                              label="sorting"
                            />
                          </div>
                        ) : (
                          column.render("Header")
                        )}
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()} className={tableBodyClasses}>
            {(page || rows).map((row, i) => {
              prepareRow(row);
              const { role, ...rowProps } = row.getRowProps();
              return (
                <React.Fragment {...rowProps}>
                  <tr
                    role={role}
                    className={classNames(
                      tableBodyRowClasses,
                      {
                        [tableBodyRowOddClasses]: i % 2 === 0,
                        [CSS_CLASSES.TABLE_BODY_ROW_WITHOUT_BOTTOM_BORDER]: row.original.rowFooter,
                      },
                      getTableBodyRowHighlightedClassName(row.original.highlighted)
                    )}
                  >
                    {row.cells.map((cell) => {
                      if (cell.column.hideOnMobile) {
                        return null;
                      }
                      return (
                        <td {...cell.getCellProps()} className={tableBodyCellClasses}>
                          {cell.render("Cell")}
                        </td>
                      );
                    })}
                  </tr>
                  {row.original.rowFooter && (
                    <tr
                      className={classNames(CSS_CLASSES.TABLE_BODY_ROW, {
                        [tableBodyRowOddClasses]: i % 2 === 0,
                      })}
                    >
                      <td colSpan={visibleColumns.length}>
                        {<div className={CSS_CLASSES.TABLE_BODY_ROW_FOOTER_CONTENT}>{rowFooterRenderer(row)}</div>}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      ) : (
        <table className={`${tableClasses} ${CSS_CLASSES.TABLE_MOBILE_ROOT}`}>
          <tbody>
            {(page || rows).map((row, i) => {
              prepareRow(row);
              return (
                <>
                  <tr
                    {...row.getRowProps()}
                    className={classNames(
                      tableMobileCardClasses,
                      {
                        [tableMobileCardOddClasses]: i % 2 === 0,
                        [CSS_CLASSES.TABLE_MOBILE_BODY_ROW_WITHOUT_BOTTOM_BORDER]: row.original.rowFooter,
                      },
                      getTableMobileCardRowHighlightedClassName(row.original.highlighted)
                    )}
                  >
                    {row.cells.map((cell) => {
                      if (cell.column.hideOnMobile) {
                        return null;
                      }
                      return (
                        <td
                          {...cell.getCellProps()}
                          className={classNames(tableMobileCardRowClasses, {
                            [tableMobileCardStripedRowClasses]: i % 2 === 0,
                          })}
                        >
                          <dl className={CSS_CLASSES.TABLE_MOBILE_CARD_ROW_KEY_VALUE_CONTAINER}>
                            <dt className={tableMobileCardRowKeyClasses}>{cell.column.render("Header")}</dt>
                            <dd className={tableMobileCardRowValueClasses}>{cell.render("Cell")}</dd>
                          </dl>
                        </td>
                      );
                    })}
                  </tr>
                  {row.original.rowFooter && (
                    <tr
                      className={classNames(CSS_CLASSES.TABLE_BODY_ROW_FOOTER, {
                        [tableMobileCardOddClasses]: i % 2 === 0,
                      })}
                    >
                      <td colSpan={visibleColumns.length}>
                        {
                          <div className={CSS_CLASSES.TABLE_MOBILE_BODY_ROW_FOOTER_CONTENT}>
                            {rowFooterRenderer(row)}
                          </div>
                        }
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}

      <table {...getTableProps()} className={`${tableClasses} ${CSS_CLASSES.TABLE_DESKTOP_ROOT}`}>
        <thead className={tableHeadClasses}>
          {headerGroups.map((headerGroup) => {
            return (
              <tr {...headerGroup.getHeaderGroupProps()} className={tableHeadRowClasses}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()} className={tableHeadCellClasses}>
                    {sortable && column.canSort ? (
                      <div className={CSS_CLASSES.TABLE_HEAD_CELL_SORTABLE}>
                        {column.render("Header")}
                        <IconButton
                          {...column.getSortByToggleProps()}
                          variant="ghost"
                          size="small"
                          icon={
                            column.isSorted ? (
                              column.isSortedDesc ? (
                                <IconDownload />
                              ) : (
                                <IconUpload />
                              )
                            ) : (
                              <IconDownload color="gray" />
                            )
                          }
                          label="sorting"
                        />
                      </div>
                    ) : (
                      column.render("Header")
                    )}
                  </th>
                ))}
              </tr>
            );
          })}
        </thead>
        <tbody {...getTableBodyProps()} className={tableBodyClasses}>
          {(page || rows).map((row, i) => {
            prepareRow(row);
            const { role, ...rowProps } = row.getRowProps();
            return (
              <React.Fragment {...rowProps}>
                <tr
                  role={role}
                  className={classNames(
                    tableBodyRowClasses,
                    {
                      [tableBodyRowOddClasses]: i % 2 === 0,
                      [CSS_CLASSES.TABLE_BODY_ROW_WITHOUT_BOTTOM_BORDER]: row.original.rowFooter,
                    },
                    getTableBodyRowHighlightedClassName(row.original.highlighted)
                  )}
                >
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()} className={tableBodyCellClasses}>
                        {cell.render("Cell")}
                      </td>
                    );
                  })}
                </tr>
                {row.original.rowFooter && (
                  <tr
                    className={classNames(CSS_CLASSES.TABLE_BODY_ROW, {
                      [tableBodyRowOddClasses]: i % 2 === 0,
                    })}
                  >
                    <td colSpan={visibleColumns.length}>
                      {<div className={CSS_CLASSES.TABLE_BODY_ROW_FOOTER_CONTENT}>{rowFooterRenderer(row)}</div>}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {paginate && (
        <TablePagination
          paginationOptions={paginationOptions}
          gotoPage={gotoPage}
          pageSize={pageSize}
          totalItems={totalItems}
          pageIndex={pageIndex}
          setPageSize={setPageSize}
        />
      )}
    </div>
  );
};
